import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Megaphone,
  Target,
  Calendar as CalendarIcon,
  MessageSquare,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  Pin,
  Users,
  Edit3,
  Trash2,
  MoreHorizontal,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import {
  fetchTeamAnnouncements,
  createTeamAnnouncement,
  updateTeamAnnouncement,
  deleteTeamAnnouncement,
  toggleAnnouncementPin,
  fetchTeamGoals,
  createTeamGoal,
  updateTeamGoal,
  updateGoalProgress,
  deleteTeamGoal,
  fetchTeamEvents,
  submitAnonymousFeedback,
  type TeamAnnouncement as DbTeamAnnouncement,
  type TeamGoal as DbTeamGoal,
  type TeamEvent as DbTeamEvent,
} from "./services/teamCommunicationService";

interface TeamAnnouncement {
  id: string;
  title: string;
  content: string;
  author: string;
  date: Date;
  pinned: boolean;
  type: "info" | "celebration" | "urgent";
  lastEditor?: string;
  lastEditedAt?: Date;
  editCount: number;
}

interface TeamGoal {
  id: string;
  title: string;
  description: string;
  progress: number;
  status: "active" | "completed" | "paused";
  dueDate: Date;
  assignedTo: string[];
  creator?: string;
  lastEditor?: string;
  lastEditedAt?: Date;
  editCount: number;
}

interface TeamEvent {
  id: string;
  title: string;
  date: Date;
  type: "meeting" | "deadline" | "celebration" | "other";
  description?: string;
}


export default function TeamCommunicationHub() {
  const { user } = useUser();
  const [announcements, setAnnouncements] = useState<TeamAnnouncement[]>([]);
  const [goals, setGoals] = useState<TeamGoal[]>([]);
  const [events, setEvents] = useState<TeamEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [editingAnnouncement, setEditingAnnouncement] = useState<TeamAnnouncement | null>(null);
  const [editingGoal, setEditingGoal] = useState<TeamGoal | null>(null);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [progressGoal, setProgressGoal] = useState<TeamGoal | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{id: string, type: 'announcement' | 'goal'} | null>(null);
  const [newProgress, setNewProgress] = useState(0);
  
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    content: "",
    type: "info" as const,
  });

  const [newGoal, setNewGoal] = useState({
    title: "",
    description: "",
    dueDate: new Date(),
  });

  const [anonymousFeedback, setAnonymousFeedback] = useState("");

  // Load team data on mount
  useEffect(() => {
    const loadTeamData = async () => {
      if (!user?.teamId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Load announcements
        const announcementsData = await fetchTeamAnnouncements(user.teamId);
        const formattedAnnouncements = announcementsData.map(a => ({
          id: a.id,
          title: a.title,
          content: a.content,
          author: a.author?.display_name || "Unknown",
          date: new Date(a.created_at),
          pinned: a.pinned,
          type: a.type,
          lastEditor: a.last_editor?.display_name,
          lastEditedAt: a.last_edited_at ? new Date(a.last_edited_at) : undefined,
          editCount: a.edit_count || 0,
        }));
        setAnnouncements(formattedAnnouncements);

        // Load goals
        const goalsData = await fetchTeamGoals(user.teamId);
        const formattedGoals = goalsData.map(g => ({
          id: g.id,
          title: g.title,
          description: g.description,
          progress: g.progress,
          status: g.status,
          dueDate: new Date(g.due_date),
          assignedTo: g.assigned_to,
          creator: g.creator?.display_name,
          lastEditor: g.last_editor?.display_name,
          lastEditedAt: g.last_edited_at ? new Date(g.last_edited_at) : undefined,
          editCount: g.edit_count || 0,
        }));
        setGoals(formattedGoals);

        // Load events for the current month
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        const endOfMonth = new Date();
        endOfMonth.setMonth(endOfMonth.getMonth() + 1);
        endOfMonth.setDate(0);
        
        const eventsData = await fetchTeamEvents(user.teamId);
        const formattedEvents = eventsData.map(e => ({
          id: e.id,
          title: e.title,
          date: new Date(e.event_date),
          type: e.event_type,
          description: e.description,
        }));
        setEvents(formattedEvents);

      } catch (error) {
        console.error("Error loading team data:", error);
        toast.error("Failed to load team data");
        // Keep empty arrays - no mock data fallback
        setAnnouncements([]);
        setGoals([]);
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTeamData();
  }, [user?.teamId]);

  const handleCreateAnnouncement = async () => {
    if (!user?.teamId || !user?.id) {
      toast.error("Team information not available");
      return;
    }
    try {
      const result = await createTeamAnnouncement(
        user.teamId,
        user.id,
        {
          title: newAnnouncement.title,
          content: newAnnouncement.content,
          type: newAnnouncement.type,
          pinned: false,
        }
      );

      if (result) {
        const announcement: TeamAnnouncement = {
          id: result.id,
          title: result.title,
          content: result.content,
          author: user.name || "Unknown",
          date: new Date(result.created_at),
          pinned: result.pinned,
          type: result.type,
        };
        
        setAnnouncements([announcement, ...announcements]);
        setNewAnnouncement({ title: "", content: "", type: "info" });
        setShowAnnouncementDialog(false);
      }
    } catch (error) {
      console.error("Error creating announcement:", error);
    }
  };

  const handleCreateGoal = async () => {
    if (!user?.teamId || !user?.id) {
      toast.error("Team information not available");
      return;
    }
    try {
      const result = await createTeamGoal(
        user.teamId,
        user.id,
        {
          title: newGoal.title,
          description: newGoal.description || "",
          progress: 0,
          status: "active",
          due_date: newGoal.dueDate.toISOString(),
          assigned_to: ["All Team"],
        }
      );

      if (result) {
        const goal: TeamGoal = {
          id: result.id,
          title: result.title,
          description: result.description || "",
          progress: result.progress,
          status: result.status,
          dueDate: new Date(result.due_date),
          assignedTo: result.assigned_to,
        };
        
        setGoals([...goals, goal]);
        setNewGoal({ title: "", description: "", dueDate: new Date() });
        setShowGoalDialog(false);
      }
    } catch (error) {
      console.error("Error creating goal:", error);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!user?.teamId) {
      toast.error("Team information not available");
      return;
    }
    try {
      const success = await submitAnonymousFeedback(user.teamId, anonymousFeedback);
      if (success) {
        setAnonymousFeedback("");
        setShowFeedbackDialog(false);
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
  };

  const togglePin = async (id: string) => {
    const announcement = announcements.find(a => a.id === id);
    if (!announcement) return;

    const success = await toggleAnnouncementPin(user.teamId, id, announcement.pinned);
    if (success) {
      setAnnouncements(announcements.map(a => 
        a.id === id ? { ...a, pinned: !a.pinned } : a
      ));
    }
  };

  const handleEditAnnouncement = (announcement: TeamAnnouncement) => {
    setEditingAnnouncement(announcement);
    setNewAnnouncement({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
    });
    setShowAnnouncementDialog(true);
  };

  const handleUpdateAnnouncement = async () => {
    if (!editingAnnouncement || !user?.id) return;

    try {
      const updatedAnnouncement = await updateTeamAnnouncement(
        user.teamId,
        editingAnnouncement.id,
        {
          title: newAnnouncement.title,
          content: newAnnouncement.content,
          type: newAnnouncement.type,
        },
        user.id
      );

      if (updatedAnnouncement) {
        const formattedAnnouncement = {
          id: updatedAnnouncement.id,
          title: updatedAnnouncement.title,
          content: updatedAnnouncement.content,
          author: updatedAnnouncement.author?.display_name || "Unknown",
          date: new Date(updatedAnnouncement.created_at),
          pinned: updatedAnnouncement.pinned,
          type: updatedAnnouncement.type,
          lastEditor: updatedAnnouncement.last_editor?.display_name,
          lastEditedAt: updatedAnnouncement.last_edited_at ? new Date(updatedAnnouncement.last_edited_at) : undefined,
          editCount: updatedAnnouncement.edit_count || 0,
        };

        setAnnouncements(announcements.map(a => 
          a.id === editingAnnouncement.id ? formattedAnnouncement : a
        ));
        setEditingAnnouncement(null);
        setNewAnnouncement({ title: "", content: "", type: "info" });
        setShowAnnouncementDialog(false);
      }
    } catch (error) {
      console.error("Error updating announcement:", error);
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    const success = await deleteTeamAnnouncement(user.teamId, id);
    if (success) {
      setAnnouncements(announcements.filter(a => a.id !== id));
    }
  };

  const handleUpdateGoalProgress = async (goalId: string, newProgress: number) => {
    if (!user?.id) return;

    const updatedGoal = await updateGoalProgress(user.teamId, goalId, newProgress);
    if (updatedGoal) {
      const formattedGoal = {
        id: updatedGoal.id,
        title: updatedGoal.title,
        description: updatedGoal.description,
        progress: updatedGoal.progress,
        status: updatedGoal.status,
        dueDate: new Date(updatedGoal.due_date),
        assignedTo: updatedGoal.assigned_to,
        creator: updatedGoal.creator?.display_name,
        lastEditor: updatedGoal.last_editor?.display_name,
        lastEditedAt: updatedGoal.last_edited_at ? new Date(updatedGoal.last_edited_at) : undefined,
        editCount: updatedGoal.edit_count || 0,
      };

      setGoals(goals.map(g => g.id === goalId ? formattedGoal : g));
      setShowProgressDialog(false);
      setProgressGoal(null);
    }
  };

  const handleDeleteGoal = async (id: string) => {
    const success = await deleteTeamGoal(user.teamId, id);
    if (success) {
      setGoals(goals.filter(g => g.id !== id));
    }
  };

  const openProgressDialog = (goal: TeamGoal) => {
    setProgressGoal(goal);
    setNewProgress(goal.progress);
    setShowProgressDialog(true);
  };

  const openDeleteDialog = (id: string, type: 'announcement' | 'goal') => {
    setItemToDelete({ id, type });
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    let success = false;
    if (itemToDelete.type === 'announcement') {
      success = await handleDeleteAnnouncement(itemToDelete.id);
    } else {
      success = await handleDeleteGoal(itemToDelete.id);
    }

    if (success) {
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }
  };

  const handleProgressUpdate = () => {
    if (progressGoal) {
      handleUpdateGoalProgress(progressGoal.id, newProgress);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "urgent":
        return "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300";
      case "celebration":
        return "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300";
      default:
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300";
    }
  };

  // Filter events to show only upcoming ones
  const upcomingEvents = events
    .filter(event => event.date >= new Date())
    .slice(0, 5)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <p>Loading team communication data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Announcements Board */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                <Megaphone className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle>Team Announcements</CardTitle>
                <CardDescription>
                  Important updates and celebrations
                </CardDescription>
              </div>
            </div>
            <Button size="sm" onClick={() => setShowAnnouncementDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Announcement
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            <div className="space-y-3">
              {announcements
                .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0))
                .map((announcement) => (
                  <Card key={announcement.id} className={cn(
                    "transition-all",
                    announcement.pinned && "border-primary"
                  )}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {announcement.pinned && (
                            <Pin className="h-4 w-4 text-primary" />
                          )}
                          <h4 className="font-medium">{announcement.title}</h4>
                          <Badge className={getTypeColor(announcement.type)}>
                            {announcement.type}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePin(announcement.id)}
                          >
                            {announcement.pinned ? "Unpin" : "Pin"}
                          </Button>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditAnnouncement(announcement)}>
                                <Edit3 className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openDeleteDialog(announcement.id, 'announcement')}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {announcement.content}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{announcement.author}</span>
                        <span>•</span>
                        <span>{format(announcement.date, "MMM d, yyyy")}</span>
                        {announcement.lastEditor && announcement.editCount > 0 && (
                          <>
                            <span>•</span>
                            <span>Edited by {announcement.lastEditor} ({announcement.editCount} {announcement.editCount === 1 ? 'edit' : 'edits'})</span>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Team Goals */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
                <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <CardTitle>Team Goals & Progress</CardTitle>
                <CardDescription>
                  Track shared objectives and milestones
                </CardDescription>
              </div>
            </div>
            <Button size="sm" onClick={() => setShowGoalDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Goal
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {goals.map((goal) => (
              <div key={goal.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{goal.title}</h4>
                    {goal.status === "completed" ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : goal.status === "paused" ? (
                      <Clock className="h-4 w-4 text-amber-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      Due {format(goal.dueDate, "MMM d")}
                    </Badge>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openProgressDialog(goal)}>
                          <Target className="h-4 w-4 mr-2" />
                          Update Progress
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => openDeleteDialog(goal.id, 'goal')}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Goal
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {goal.description}
                </p>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span>Progress</span>
                    <span>{goal.progress}%</span>
                  </div>
                  <Progress value={goal.progress} className="h-2" />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Users className="h-3 w-3" />
                    <span>{goal.assignedTo.join(", ")}</span>
                  </div>
                  {(goal.creator || goal.lastEditor) && (
                    <div className="flex items-center gap-1">
                      {goal.creator && <span>Created by {goal.creator}</span>}
                      {goal.lastEditor && goal.editCount > 0 && (
                        <>
                          {goal.creator && <span>•</span>}
                          <span>Last edited by {goal.lastEditor} ({goal.editCount} {goal.editCount === 1 ? 'edit' : 'edits'})</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Team Calendar */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
                <CalendarIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <CardTitle>Team Calendar</CardTitle>
                <CardDescription>
                  Important dates and deadlines
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
            />
            <div className="mt-4 space-y-2">
              <h4 className="font-medium text-sm">Upcoming Events</h4>
              {upcomingEvents.map((event) => (
                <div key={event.id} className="flex items-center justify-between text-sm">
                  <span>{event.title}</span>
                  <Badge variant="outline">
                    {format(event.date, "MMM d")}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Anonymous Feedback */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                <MessageSquare className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <CardTitle>Anonymous Feedback</CardTitle>
                <CardDescription>
                  Share concerns or suggestions privately
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Your feedback helps us improve team dynamics and address concerns.
                All submissions are completely anonymous.
              </p>
              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => setShowFeedbackDialog(true)}
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                Submit Anonymous Feedback
              </Button>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium text-sm mb-2">Recent Team Actions</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Implemented flexible work hours</li>
                  <li>• Added weekly team sync meetings</li>
                  <li>• Improved project documentation</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <AlertDialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {editingAnnouncement ? 'Update your team announcement' : 'Share important updates with your team'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                placeholder="Announcement title"
              />
            </div>
            <div>
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={newAnnouncement.content}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                placeholder="Write your announcement..."
                rows={4}
              />
            </div>
            <div>
              <Label>Type</Label>
              <div className="flex gap-2 mt-2">
                {["info", "celebration", "urgent"].map((type) => (
                  <Button
                    key={type}
                    variant={newAnnouncement.type === type ? "default" : "outline"}
                    size="sm"
                    onClick={() => setNewAnnouncement({ ...newAnnouncement, type: type as any })}
                  >
                    {type}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setEditingAnnouncement(null);
              setNewAnnouncement({ title: "", content: "", type: "info" });
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={editingAnnouncement ? handleUpdateAnnouncement : handleCreateAnnouncement}>
              {editingAnnouncement ? 'Update Announcement' : 'Post Announcement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create Team Goal</AlertDialogTitle>
            <AlertDialogDescription>
              Set a new objective for your team to work towards
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="goal-title">Goal Title</Label>
              <Input
                id="goal-title"
                value={newGoal.title}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                placeholder="What do you want to achieve?"
              />
            </div>
            <div>
              <Label htmlFor="goal-description">Description</Label>
              <Textarea
                id="goal-description"
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                placeholder="Describe the goal and success criteria..."
                rows={3}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(newGoal.dueDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={newGoal.dueDate}
                    onSelect={(date) => date && setNewGoal({ ...newGoal, dueDate: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreateGoal}>
              Create Goal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Anonymous Feedback</AlertDialogTitle>
            <AlertDialogDescription>
              Your identity will not be recorded or shared. This is a safe space to share concerns.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <Textarea
              value={anonymousFeedback}
              onChange={(e) => setAnonymousFeedback(e.target.value)}
              placeholder="Share your thoughts, concerns, or suggestions..."
              rows={6}
            />
            <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
              <p className="font-medium mb-1">Your feedback is anonymous</p>
              <p>No identifying information is collected or stored with your submission.</p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmitFeedback}>
              Submit Feedback
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Progress Update Dialog */}
      <AlertDialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Goal Progress</AlertDialogTitle>
            <AlertDialogDescription>
              {progressGoal && `Update the progress for "${progressGoal.title}"`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="progress">Progress ({newProgress}%)</Label>
              <Input
                id="progress"
                type="range"
                min="0"
                max="100"
                value={newProgress}
                onChange={(e) => setNewProgress(parseInt(e.target.value))}
                className="w-full"
              />
              <Progress value={newProgress} className="mt-2" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowProgressDialog(false);
              setProgressGoal(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleProgressUpdate}>
              Update Progress
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {itemToDelete?.type}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteDialog(false);
              setItemToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}